import { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector, shallowEqual } from 'react-redux';
import { useIntl } from 'react-intl';
import axios from 'axios';

import { SetConfigurationChanges } from 'store/ducks/configuration.duck';
import { successToast, errorToast } from 'utils/toastUtils';

// Tag outbound writes made during a Save so the global response interceptor
// in AuthenticatedApp skips refetching the server change-count — it increments
// per PATCH, clobbering our optimistic delta on multi-PATCH Saves.
export const SUPPRESS_CHANGES_REFETCH_HEADER = 'X-Suppress-Change-Refetch';

/**
 * Thrown from a consumer's `runAll` when some operations in a batch
 * succeeded and others failed. The hook recognises this and emits a
 * different toast, and crucially does NOT dispatch the optimistic counter
 * delta or call the consumer's resetForm — Formik state is left in
 * "partially-pruned" shape so a retry only re-fires the still-failing ops.
 */
export class PartialFailureError extends Error {
  constructor({ succeeded = 0, failed = 0, total = 0 } = {}) {
    super(`partial save failure: ${failed}/${total} operations failed`);
    this.name = 'PartialFailureError';
    this.__partialFailure = true;
    this.succeeded = succeeded;
    this.failed = failed;
    this.total = total;
  }
}

let activeSaveDepth = 0;
let requestInterceptorId = null;

// Subscribers notified whenever activeSaveDepth changes. Used by
// `useIsAnySaveInFlight` so the AdvanceConfiguration footer's Save/Discard
// buttons can disable while ANY hook instance has an in-flight save —
// across consumer boundaries (WebFilter, IPS, Antivirus, etc.). This
// signal is reliable because depth is mutated only by acquire/release,
// which run inside the hook's try/finally block; partial-failure paths
// can't leave it stuck (unlike Formik's `isSubmitting`, which has known
// reset-race issues with `enableReinitialize`).
const _depthSubscribers = new Set();

const _notifyDepthChange = () => {
  _depthSubscribers.forEach((fn) => fn(activeSaveDepth));
};

export const useIsAnySaveInFlight = () => {
  const [count, setCount] = useState(activeSaveDepth);
  useEffect(() => {
    _depthSubscribers.add(setCount);
    // Resync in case depth changed between render and subscribe.
    setCount(activeSaveDepth);
    return () => {
      _depthSubscribers.delete(setCount);
    };
  }, []);
  return count > 0;
};

const acquireSaveSuppression = () => {
  if (activeSaveDepth === 0) {
    requestInterceptorId = axios.interceptors.request.use((config) => {
      if (config.method && config.method.toLowerCase() !== 'get') {
        config.headers = config.headers || {};
        config.headers[SUPPRESS_CHANGES_REFETCH_HEADER] = '1';
      }
      return config;
    });
  }
  activeSaveDepth += 1;
  _notifyDepthChange();
};

const releaseSaveSuppression = () => {
  activeSaveDepth = Math.max(0, activeSaveDepth - 1);
  if (activeSaveDepth === 0 && requestInterceptorId !== null) {
    axios.interceptors.request.eject(requestInterceptorId);
    requestInterceptorId = null;
  }
  _notifyDepthChange();
};

// Test-only: reset module-level state so jest tests don't leak depth across
// each other. Real code never imports this. Production behaviour is unaffected.
export const __resetForTesting = () => {
  activeSaveDepth = 0;
  if (requestInterceptorId !== null) {
    try { axios.interceptors.request.eject(requestInterceptorId); } catch {}
    requestInterceptorId = null;
  }
  _notifyDepthChange();
};

const useProfileSaveHandler = ({
  successMessage = 'GENERAL.SAVE_SUCCESS',
  errorMessage = 'GENERAL.ERROR',
  partialFailureMessage = 'GENERAL.PARTIAL_SAVE_FAILURE',
} = {}) => {
  const intl = useIntl();
  const dispatch = useDispatch();
  // Re-entry guard: prevents a second click from firing a parallel save while
  // the first is still in flight. Pairs with `isSaving` so the UI can also
  // disable the trigger button visually.
  const inFlightRef = useRef(false);
  const [isSaving, setIsSaving] = useState(false);

  const { changeCount } = useSelector(
    ({ configuration }) => ({ changeCount: configuration.changeCount }),
    shallowEqual
  );

  const save = useCallback(
    /**
     * @param {(values: any) => Promise<any>} apiCall
     * @param {any} values
     * @param {{ resetForm?: (opts: { values: any }) => any, expectedDelta?: number }} [helpers]
     */
    async (apiCall, values, helpers) => {
      if (inFlightRef.current) return;
      inFlightRef.current = true;
      setIsSaving(true);
      acquireSaveSuppression();
      try {
        await apiCall(values);
        const delta = Math.max(1, helpers?.expectedDelta ?? 1);
        dispatch(SetConfigurationChanges(changeCount + delta));
        successToast({ body: successMessage, intl });
        // Await resetForm — consumers like WebFilter / IPS pass an async
        // callback that refetches lists before calling Formik's resetForm.
        // Without the await, the `finally` below releases the in-flight
        // depth (re-enabling the Save button) BEFORE the form is reset,
        // leaving a window — visible on slow networks (3G) — where the
        // user can click Save twice and fire a duplicate batch.
        if (helpers?.resetForm) await helpers.resetForm({ values });
      } catch (err) {
        if (err?.__partialFailure) {
          // Format the n/total info into the body. We try the i18n key first;
          // if it's missing, fall back to a literal so the user still sees
          // *something* useful.
          let body;
          try {
            body = intl.formatMessage(
              { id: partialFailureMessage },
              { failed: err.failed, total: err.total }
            );
          } catch {
            body = `${err.failed}/${err.total} operations failed; retry`;
          }
          errorToast({ body, intl });
        } else {
          const body = err?.response?.data?.error || errorMessage;
          errorToast({ body, intl });
        }
        // Neither path dispatches the counter delta — only fully-successful
        // saves bump the navbar counter.
      } finally {
        releaseSaveSuppression();
        inFlightRef.current = false;
        setIsSaving(false);
      }
    },
    [dispatch, changeCount, intl, successMessage, errorMessage, partialFailureMessage]
  );

  // Tag the callable with `isSaving` so existing `const save = useProfileSaveHandler(...)`
  // call sites keep working (callable-only), while new code can read `save.isSaving`
  // for spinner / disabled-button UX. No tuple unpacking required.
  save.isSaving = isSaving;
  return save;
};

export default useProfileSaveHandler;
